import React, { useState, useEffect, useCallback } from 'react'
import type { Block } from '../../editor/types'

interface QuizBlockProps {
  block: Block
  demoId?: number
  onSetVariableValue?: (name: string, value: string) => void
}

interface QuizOption {
  label: string
  value: string
}

function getConfig(block: Block) {
  const config = (block.config || {}) as Record<string, unknown>
  return {
    options: (config.options as QuizOption[]) || [],
    correctValue: (config.correctValue as string) || '',
    explanation: (config.explanation as string) || '',
    showExplanation: (config.showExplanation as boolean) ?? true,
    allowRetry: (config.allowRetry as boolean) ?? true,
  }
}

export default function QuizBlock({ block, demoId, onSetVariableValue }: QuizBlockProps) {
  const { options, correctValue, explanation, showExplanation, allowRetry } = getConfig(block)
  const accent = block.accent || 'var(--accent, #5048e5)'

  const [selectedValue, setSelectedValue] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)

  const storageKey = `explaina_quiz_${demoId}_${block.id}`

  // Load saved answer from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        setSelectedValue(saved)
        setSubmitted(true)
        if (block.variable && onSetVariableValue) onSetVariableValue(block.variable, saved)
      }
    } catch { /* ignore */ }
  }, [storageKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = useCallback((value: string) => {
    if (submitted) return
    setSelectedValue(value)
    setSubmitted(true)
    if (block.variable && onSetVariableValue) onSetVariableValue(block.variable, value)
    try { localStorage.setItem(storageKey, value) } catch { /* ignore */ }
  }, [submitted, storageKey, block.variable, onSetVariableValue])

  const handleRetry = useCallback(() => {
    setSelectedValue(null)
    setSubmitted(false)
    if (block.variable && onSetVariableValue) onSetVariableValue(block.variable, '')
    try { localStorage.removeItem(storageKey) } catch { /* ignore */ }
  }, [storageKey, block.variable, onSetVariableValue])

  const isCorrect = selectedValue === correctValue

  return (
    <div className="space-y-3">
      {block.label && (
        <p className="text-base font-semibold text-slate-900">{block.label}</p>
      )}

      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(options.length, 3)}, 1fr)` }}>
        {options.map((opt) => {
          const isSelected = selectedValue === opt.value
          const isCorrectOption = opt.value === correctValue

          let borderColor = '#e2e8f0'
          let bgColor = 'white'
          let textColor: string | undefined = undefined
          let textClass = 'text-slate-700'

          if (submitted) {
            if (isCorrectOption) {
              borderColor = '#10b981'
              bgColor = '#10b98110'
              textColor = '#10b981'
              textClass = ''
            } else if (isSelected) {
              borderColor = '#ef4444'
              bgColor = '#ef444410'
              textColor = '#ef4444'
              textClass = ''
            } else {
              borderColor = '#e2e8f0'
              bgColor = '#f8fafc'
              textClass = 'text-slate-400'
            }
          }

          return (
            <button
              key={opt.value}
              onClick={() => handleSelect(opt.value)}
              disabled={submitted}
              className={`relative overflow-hidden rounded-xl transition-all ${
                submitted ? 'cursor-default' : 'cursor-pointer hover:shadow-md hover:-translate-y-0.5'
              }`}
              style={{
                border: `2px solid ${borderColor}`,
                background: bgColor,
              }}
            >
              <div className="px-4 py-3 text-center flex items-center justify-center gap-2">
                {submitted && isCorrectOption && (
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500 text-white text-xs shrink-0">
                    ✓
                  </span>
                )}
                {submitted && isSelected && !isCorrectOption && (
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-white text-xs shrink-0">
                    ✗
                  </span>
                )}
                <span
                  className={`text-sm font-semibold ${textClass}`}
                  style={textColor ? { color: textColor } : undefined}
                >
                  {opt.label}
                </span>
              </div>
            </button>
          )
        })}
      </div>

      {submitted && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-xl ${
          isCorrect ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
        }`}>
          <span className="text-lg">{isCorrect ? '✓' : '✗'}</span>
          <span className="text-sm font-semibold">
            {isCorrect ? 'Correct!' : 'Incorrect'}
          </span>
        </div>
      )}

      {submitted && showExplanation && explanation && (
        <div className="px-4 py-3 rounded-xl bg-slate-50 border border-slate-200">
          <p className="text-sm text-slate-600">{explanation}</p>
        </div>
      )}

      {submitted && allowRetry && (
        <button
          onClick={handleRetry}
          className="text-xs font-medium text-slate-400 hover:text-slate-600 transition-colors"
        >
          Try again
        </button>
      )}
    </div>
  )
}
